/**
 * The finer points of a credit card: network and tier (Visa Signature, World Elite…), charge
 * cards with no preset limit, business or personal, Amex-style statements that close a set
 * number of days before the due date, welcome bonuses, and which points program it earns into.
 * Known cards fill these in from their name; you can change any of them.
 */
import type { IsoDate } from "../types.ts";
import type { CardRewards } from "./rewards.ts";

export type Network = "visa" | "mastercard" | "amex" | "discover";

export interface WelcomeBonus {
  /** Spend this much… */
  spendCents: number;
  /** …between these dates… */
  from: IsoDate;
  by: IsoDate;
  /** …to get this (points, or cashback in cents). */
  reward: number;
  unit: "points" | "cash";
  /** Spending that counts but isn't logged in your groups (cents). */
  offsetCents?: number;
  /** You've received it. */
  received?: boolean;
}

export interface CardDetails {
  network?: Network;
  /** "Visa Signature", "World Elite Mastercard", "Platinum"… */
  tier?: string;
  /** A charge card (like Amex Green or Gold): no preset spending limit, paid in full. */
  charge?: boolean;
  business?: boolean;
  /**
   * When the statement closes: the same day each month (the card's statement day), or a set
   * number of days before the due date, which is how American Express does it.
   */
  cycle?: { mode: "monthly" } | { mode: "before-due"; days: number };
  welcome?: WelcomeBonus | null;
  /** The points program it earns into (see MoneyProgram), like Amex Membership Rewards. */
  programId?: string | null;
}

/** A points or cashback balance shared by cards, or a portal like Rakuten. */
export interface MoneyProgram {
  id: string;
  name: string;
  unit: "points" | "cash";
  /** Points, or cents for cashback. */
  posted: number;
  pending: number;
  asOf: IsoDate;
  /** What a point is worth in cents. */
  pointCents: number;
  /** Where it pays out (Rakuten can pay into Amex Membership Rewards). */
  paysTo: string | null;
  note: string;
}

export const NETWORKS: { id: Network; label: string; tiers: string[] }[] = [
  { id: "visa", label: "Visa", tiers: ["Visa Traditional", "Visa Signature", "Visa Infinite"] },
  {
    id: "mastercard",
    label: "Mastercard",
    tiers: ["Mastercard Standard", "World Mastercard", "World Elite Mastercard"],
  },
  { id: "amex", label: "American Express", tiers: [] },
  { id: "discover", label: "Discover", tiers: [] },
];

export const networkLabel = (n: Network | undefined) =>
  NETWORKS.find((x) => x.id === n)?.label ?? "";

interface Known {
  match: RegExp;
  issuer: string;
  name: string;
  details: CardDetails;
  rewards?: CardRewards;
}

const MR = { unit: "points", pointCents: 2 } as const;
const UR = { unit: "points", pointCents: 1.5 } as const;

/** Popular cards, so picking one fills in the network, tier, charge/business and earn rates. */
export const KNOWN_CARDS: Known[] = [
  {
    match: /blue business plus|\bbbp\b/i,
    issuer: "Amex",
    name: "Blue Business Plus",
    details: { network: "amex", business: true },
    rewards: {
      ...MR,
      rates: { other: 1 },
      caps: [
        {
          label: "2x on the first $50,000 a year",
          rates: { other: 2 },
          capCents: 5_000_000,
          period: "year",
          after: 1,
        },
      ],
      source: "you",
    },
  },
  {
    match: /blue business cash/i,
    issuer: "Amex",
    name: "Blue Business Cash",
    details: { network: "amex", business: true },
    rewards: {
      unit: "cash",
      rates: { other: 1 },
      caps: [
        {
          label: "2% on the first $50,000 a year",
          rates: { other: 2 },
          capCents: 5_000_000,
          period: "year",
          after: 1,
        },
      ],
      source: "you",
    },
  },
  {
    match: /business gold/i,
    issuer: "Amex",
    name: "Business Gold",
    details: { network: "amex", charge: true, business: true },
  },
  {
    match: /business platinum/i,
    issuer: "Amex",
    name: "Business Platinum",
    details: { network: "amex", charge: true, business: true },
    rewards: { ...MR, rates: { travel: 5, other: 1 }, source: "you" },
  },
  {
    match: /amex green|^green$|classic green|\bgreen card\b/i,
    issuer: "Amex",
    name: "Green",
    details: { network: "amex", charge: true },
    rewards: { ...MR, rates: { dining: 3, travel: 3, other: 1 }, source: "you" },
  },
  {
    match: /amex gold|^gold$|gold card/i,
    issuer: "Amex",
    name: "Gold",
    details: { network: "amex", charge: true },
    rewards: {
      ...MR,
      rates: { dining: 4, travel: 3, other: 1 },
      caps: [
        {
          label: "4x at US supermarkets, first $25,000 a year",
          rates: { grocery: 4 },
          capCents: 2_500_000,
          period: "year",
          after: 1,
        },
      ],
      source: "you",
    },
  },
  {
    match: /amex platinum|^platinum$|platinum card/i,
    issuer: "Amex",
    name: "Platinum",
    details: { network: "amex", charge: true },
    rewards: { ...MR, rates: { travel: 5, other: 1 }, source: "you" },
  },
  {
    match: /blue cash preferred/i,
    issuer: "Amex",
    name: "Blue Cash Preferred",
    details: { network: "amex" },
    rewards: {
      unit: "cash",
      rates: { streaming: 6, gas: 3, travel: 3, other: 1 },
      caps: [
        {
          label: "6% at US supermarkets, first $6,000 a year",
          rates: { grocery: 6 },
          capCents: 600_000,
          period: "year",
          after: 1,
        },
      ],
      source: "you",
    },
  },
  {
    match: /customized cash|\bccr\b/i,
    issuer: "Bank of America",
    name: "Customized Cash Rewards",
    details: { network: "visa", tier: "Visa Signature" },
    rewards: {
      unit: "cash",
      rates: { other: 1 },
      caps: [
        {
          label:
            "3% in your choice category and 2% at grocery and warehouse clubs, first $2,500 a quarter combined",
          rates: { online: 3, grocery: 2, wholesale: 2 },
          capCents: 250_000,
          period: "quarter",
          after: 1,
        },
      ],
      source: "you",
    },
  },
  {
    match: /unlimited cash rewards/i,
    issuer: "Bank of America",
    name: "Unlimited Cash Rewards",
    details: { network: "visa", tier: "Visa Signature" },
    rewards: { unit: "cash", rates: { other: 1.5 }, source: "you" },
  },
  {
    match: /savor/i,
    issuer: "Capital One",
    name: "Savor",
    details: { network: "mastercard", tier: "World Elite Mastercard" },
    rewards: {
      unit: "cash",
      rates: { dining: 3, grocery: 3, entertainment: 3, streaming: 3, other: 1 },
      source: "you",
    },
  },
  {
    match: /venture x/i,
    issuer: "Capital One",
    name: "Venture X",
    details: { network: "visa", tier: "Visa Infinite" },
    rewards: { unit: "points", pointCents: 1.5, rates: { other: 2 }, source: "you" },
  },
  {
    match: /sapphire preferred|\bcsp\b/i,
    issuer: "Chase",
    name: "Sapphire Preferred",
    details: { network: "visa", tier: "Visa Signature" },
    rewards: { ...UR, rates: { dining: 3, streaming: 3, travel: 2, other: 1 }, source: "you" },
  },
  {
    match: /sapphire reserve|\bcsr\b/i,
    issuer: "Chase",
    name: "Sapphire Reserve",
    details: { network: "visa", tier: "Visa Infinite" },
    rewards: { ...UR, rates: { dining: 3, travel: 3, other: 1 }, source: "you" },
  },
  {
    match: /freedom unlimited|\bcfu\b/i,
    issuer: "Chase",
    name: "Freedom Unlimited",
    details: { network: "visa", tier: "Visa Signature" },
    rewards: { ...UR, rates: { dining: 3, drugstore: 3, other: 1.5 }, source: "you" },
  },
  {
    match: /freedom flex/i,
    issuer: "Chase",
    name: "Freedom Flex",
    details: { network: "mastercard", tier: "World Elite Mastercard" },
    rewards: { ...UR, rates: { dining: 3, drugstore: 3, other: 1 }, source: "you" },
  },
  {
    match: /double cash/i,
    issuer: "Citi",
    name: "Double Cash",
    details: { network: "mastercard", tier: "World Elite Mastercard" },
    rewards: { unit: "cash", rates: { other: 2 }, source: "you" },
  },
  {
    match: /costco anywhere|costco visa/i,
    issuer: "Citi",
    name: "Costco Anywhere Visa",
    details: { network: "visa", tier: "Visa Signature" },
    rewards: {
      unit: "cash",
      rates: { travel: 3, dining: 3, wholesale: 2, other: 1 },
      source: "you",
    },
  },
  {
    match: /discover it/i,
    issuer: "Discover",
    name: "Discover it",
    details: { network: "discover" },
    rewards: { unit: "cash", rates: { other: 1 }, source: "you" },
  },
];

/** What we know about a card from its name (and issuer), or null for one we don't know. */
export function knownCard(name: string, issuer = ""): Known | null {
  const text = `${issuer} ${name}`.trim();
  return KNOWN_CARDS.find((k) => k.match.test(name) || k.match.test(text)) ?? null;
}

/** The network a card most likely runs on, from the issuer alone. */
export function networkFromIssuer(issuer: string): Network | undefined {
  const s = issuer.toLowerCase();
  if (/amex|american express/.test(s)) return "amex";
  if (/discover/.test(s)) return "discover";
  return undefined;
}

/** Amex closes each statement a set number of days before the due date. */
export const AMEX_CLOSE_DAYS = 25;

function addDays(iso: IsoDate, n: number): IsoDate {
  return new Date(Date.parse(`${iso}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

function dueOn(y: number, m0: number, day: number): IsoDate {
  const last = new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m0, Math.min(day, last))).toISOString().slice(0, 10);
}

/** Statement closes for a card that closes `days` before each due date: the last one and the next. */
export function cycleBeforeDue(
  dueDay: number,
  days: number,
  today: IsoDate,
): { lastClose: IsoDate; nextClose: IsoDate } {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7)) - 1;
  const closes = [-2, -1, 0, 1, 2].map((k) => addDays(dueOn(y, m + k, dueDay), -days));
  const nextClose = closes.find((c) => c > today)!;
  const lastClose = [...closes].reverse().find((c) => c <= today)!;
  return { lastClose, nextClose };
}

/**
 * From a statement close date you saw, how many days before the due date this card closes
 * (the first due date after it). Returns null when the gap isn't believable (10–40 days).
 */
export function closeDaysFrom(closedOn: IsoDate, dueDay: number): number | null {
  const y = Number(closedOn.slice(0, 4));
  const m = Number(closedOn.slice(5, 7)) - 1;
  const due = [0, 1, 2].map((k) => dueOn(y, m + k, dueDay)).find((d) => d > closedOn)!;
  const gap = Math.round(
    (Date.parse(`${due}T12:00:00Z`) - Date.parse(`${closedOn}T12:00:00Z`)) / 86_400_000,
  );
  return gap >= 10 && gap <= 40 ? gap : null;
}

export interface WelcomeProgress {
  spentCents: number;
  leftCents: number;
  pct: number;
  daysLeft: number;
  done: boolean;
  expired: boolean;
}

/** How far along a welcome bonus is, from purchases logged in its window plus any offset. */
export function welcomeProgress(
  w: WelcomeBonus,
  purchases: readonly { date: IsoDate; totalMicro: number }[],
  today: IsoDate,
): WelcomeProgress {
  const logged = purchases
    .filter((p) => p.date >= w.from && p.date <= w.by)
    .reduce((s, p) => s + p.totalMicro, 0);
  const spentCents = Math.round(logged / 10_000) + (w.offsetCents ?? 0);
  const leftCents = Math.max(0, w.spendCents - spentCents);
  const daysLeft = Math.round(
    (Date.parse(`${w.by}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86_400_000,
  );
  const done = !!w.received || leftCents === 0;
  return {
    spentCents,
    leftCents,
    pct: w.spendCents ? Math.min(100, Math.round((spentCents / w.spendCents) * 100)) : 100,
    daysLeft,
    done,
    expired: !done && daysLeft < 0,
  };
}

/** One line describing the card: "Visa Signature · Business · Charge card". */
export function cardKindLine(d: CardDetails | null | undefined, issuer = ""): string {
  if (!d) return "";
  // "Amex · American Express" says the same thing twice.
  const sameAsIssuer = !d.tier && d.network && networkFromIssuer(issuer) === d.network;
  return [
    d.tier || (sameAsIssuer ? "" : networkLabel(d.network)),
    d.business ? "Business" : "",
    d.charge ? "Charge card" : "",
  ]
    .filter(Boolean)
    .join(" · ");
}
